import _ from "lodash";
import {useMemo} from "react";
import {useAtomValue} from "jotai";
import {ALL_ACCESS_FUNCTIONS, isUserHaveTransitivelyAnyOfCompanyRoles} from "../utils/accessControlUtils";
import {useAccessMarker} from "./accessMarkerHooks";
import {authenticatedUserLoadable} from "../store/authenticatedUserStore";
import {CompanyMemberRole} from "../types/domain/companyMember";
import {isResourceSucceeded} from "../utils/resourceUtils";
import {ResourceException} from "../types/resourceError";
import {useQuery} from "@tanstack/react-query";
import {getCompanies, getCompanyById, getCompanyHierarchy} from "../services/companyService";
import {Company} from "../types/domain/company";
import {useParams, useSearchParams} from "react-router-dom";
import {loadLastCompanyId, saveLastCompanyId} from "../utils/localStorageUtils";
import {getUserCompanies} from "../services/userService";
import {DEMO_COMPANY_ID} from "../utils/constants";

const ACCESS_FUNCTIONS = _.pick(ALL_ACCESS_FUNCTIONS, ["canGetAllCompanies"]);

export const useCompanies = () => {
    return useQuery<Company[], ResourceException>({
        queryKey: ["companies"],
        queryFn: () => getCompanies(),
        gcTime: Infinity,
        staleTime: Infinity,
    });
};

/**
 * Retrieving Active Company Id. The logic of companyId retrieval (ordered):
 * 1. Obtain companyId from URL, for instance /organization/{companyId}/users
 * 2. Obtain companyId from the latestCompanyId value in browser's Local Store
 * 3. Obtain companyId of the first company of the user he is a member of
 * 4. Return Demo Company Id
 **/
export const useActiveCompanyId = (): string => {
    const {companyId} = useParams();
    const companyIdFromLocalStorage = loadLastCompanyId();
    const userCompanies = useUserCompanies();

    const activeCompanyId = companyId ?? companyIdFromLocalStorage ?? userCompanies?.data?.[0]?.id ?? DEMO_COMPANY_ID;

    saveLastCompanyId(activeCompanyId);

    return activeCompanyId;
};

export const useCompany = () => {
    const activeCompanyId = useActiveCompanyId();

    return useQuery<Company, ResourceException>({
        queryKey: ["company", activeCompanyId],
        queryFn: () => getCompanyById(activeCompanyId),
        enabled: !!activeCompanyId,
        gcTime: Infinity,
        staleTime: Infinity,
    });
};

export const useParentCompanyParam = () => {
    const [searchParams] = useSearchParams();
    return searchParams.get("parentCompany") ?? undefined;
};

export const useParentCompany = () => {
    const parentCompanyId = useParentCompanyParam();

    return useQuery<Company, ResourceException>({
        queryKey: ["parentCompany", parentCompanyId],
        queryFn: () => getCompanyById(parentCompanyId!),
        enabled: !!parentCompanyId,
        gcTime: Infinity,
        staleTime: Infinity,
    });
};

export const useUserCompanies = ({
    userId,
    includeChildCompanies = false,
}: {
    userId?: string;
    includeChildCompanies?: boolean;
} = {}) => {
    const authenticatedUserState = useAtomValue(authenticatedUserLoadable);

    const _userId =
        userId ?? (authenticatedUserState.state === "hasData" ? authenticatedUserState.data?.id : undefined);

    return useQuery<Company[], ResourceException>({
        queryKey: ["userCompanies", _userId, includeChildCompanies],
        queryFn: () => getUserCompanies(_userId!, {includeChildCompanies}),
        enabled: !!_userId,
        gcTime: Infinity,
        staleTime: Infinity,
    });
};

/**
 * Provides a list of companies eligible for selection as parent company
 * ADMIN and CS can select (as a parent) any company
 * MANAGER can select (as a parent) only company, for which he has MANAGER permission
 */
export const useAvailableParentCompanies = () => {
    const access = useAccessMarker(ACCESS_FUNCTIONS);
    const userResource = useAtomValue(authenticatedUserLoadable);
    const companies = useCompanies();
    const userCompanies = useUserCompanies({includeChildCompanies: true});
    const foundCompanies = access.canGetAllCompanies ? companies : userCompanies;

    return useMemo(() => {
        if (foundCompanies.isSuccess && isResourceSucceeded(userResource) && !access.canGetAllCompanies) {
            return {
                ...foundCompanies,
                data: foundCompanies?.data?.filter((company) =>
                    isUserHaveTransitivelyAnyOfCompanyRoles(userResource.data, company, [CompanyMemberRole.MANAGER]),
                ),
            };
        }
        return foundCompanies;
    }, [access.canGetAllCompanies, foundCompanies, userResource]);
};

export const useCompanyHierarchy = () => {
    const activeCompanyId = useActiveCompanyId();
    return useQuery<Company[], ResourceException>({
        queryKey: ["companyHierarchy", activeCompanyId],
        queryFn: () => getCompanyHierarchy(activeCompanyId),
        enabled: !!activeCompanyId,
        gcTime: Infinity,
        staleTime: Infinity,
    });
};
